import { getMomentTz, MesfModal, TimeService } from "@dexteel/mesf-core";
import {
	Alert,
	Box,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import type {
	AffectedPassChange,
	PassChangeSummary,
} from "../../repositories/CarrierAssemblyRepository";

const moment = getMomentTz();

const formatDate = (date: Date | null) => {
	if (!date) return "-";
	return moment(date)
		.tz(TimeService.getInstance().getServerTimeZone())
		.format("MM/DD/YYYY HH:mm:ss");
};

type Props = {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
	affectedPasses: AffectedPassChange[];
	summary: PassChangeSummary[];
};

export const ConfirmPassDeletionModal = ({
	open,
	onClose,
	onConfirm,
	affectedPasses,
	summary,
}: Props) => {
	const allPassesDeletedRolls = summary.filter(
		(s) => s.AffectedCount === s.TotalActiveCount,
	);
	const canConfirm = allPassesDeletedRolls.length === 0;

	return (
		<MesfModal
			open={open}
			handleClose={onClose}
			title="Pass Changes Will Be Deleted"
			maxWidth="md"
			id="confirm-pass-deletion-modal"
		>
			<MesfModal.Content dividers style={{ padding: "20px 30px" }}>
				<Box>
					<Typography sx={{ marginBottom: 2 }}>
						The following pass changes occurred after the uninstall time and
						will be deleted:
					</Typography>

					{allPassesDeletedRolls.length > 0 && (
						<Alert severity="error" sx={{ marginBottom: 2 }}>
							{allPassesDeletedRolls.map((roll) => (
								<div key={roll.EdgerRollPartId}>
									Cannot proceed: all pass records for{" "}
									<strong>{roll.EdgerRollName}</strong> would be deleted. At
									least one pass record must remain per roll.
								</div>
							))}
						</Alert>
					)}

					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>
										<strong>Roll</strong>
									</TableCell>
									<TableCell>
										<strong>Pass #</strong>
									</TableCell>
									<TableCell>
										<strong>Started At</strong>
									</TableCell>
									<TableCell>
										<strong>Ended At</strong>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{affectedPasses.map((p) => (
									<TableRow key={p.CarrierInstancePassId}>
										<TableCell>{p.EdgerRollName}</TableCell>
										<TableCell>{p.PassNumber}</TableCell>
										<TableCell>{formatDate(p.StartedAt)}</TableCell>
										<TableCell>{formatDate(p.EndedAt)}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</MesfModal.Content>
			<MesfModal.Actions style={{ padding: "15px 30px" }}>
				<Button variant="contained" color="inherit" onClick={onClose}>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="secondary"
					onClick={onConfirm}
					disabled={!canConfirm}
				>
					Confirm Delete
				</Button>
			</MesfModal.Actions>
		</MesfModal>
	);
};
